import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Users, Landmark, FileText,
  MapPin, Library, Calendar, BarChart3, Vote, Settings, Building2, History, Gauge, ThumbsDown,
  ScrollText,
  type LucideIcon,
} from 'lucide-react';
import { Topbar } from './Topbar';
import { Tooltip } from '../ui/Tooltip';
import { ResearchAnchorBanner } from '../ResearchAnchorBanner';
import { NavigationTrail } from '../NavigationTrail';
import { useTrailStore } from '../../stores/trailStore';
import { useChatStore } from '../../stores/chatStore';
import { useT } from '../../i18n';
import { useIsDeveloper } from '../../hooks/useIsDeveloper';
import DockLeftIcon from '../ui/icons/DockLeftIcon';

// ---- Sidebar ----
interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
}

const baseNavItems = [
  { id: 'overview',  icon: BarChart3, labelKey: 'nav.overview',  path: '/'          },
  { id: 'members',   icon: Users,     labelKey: 'nav.members',   path: '/members'   },
  { id: 'debates',   icon: FileText,  labelKey: 'nav.debates',   path: '/debates'   },
  { id: 'bills',     icon: Library,   labelKey: 'nav.bills',     path: '/bills'     },
  { id: 'votes',     icon: Vote,      labelKey: 'nav.votes',     path: '/votes'     },
  { id: 'parties',   icon: MapPin,    labelKey: 'nav.parties',   path: '/parties'   },
  { id: 'sessions',  icon: Calendar,  labelKey: 'nav.sessions',  path: '/sessions'  },
  { id: 'committees', icon: Building2, labelKey: 'nav.committees', path: '/committees' },
  { id: 'chatHistory', icon: History, labelKey: 'nav.chatHistory', path: '/chat-history' },
  { id: 'reports', icon: ScrollText, labelKey: 'nav.reports', path: '/reports' },
];

// Developer-only entry — appended below Chat History when the current Clerk
// user has privateMetadata.role = 'developer'. Backend gates the underlying
// /db/chat/admin/usage endpoint on the same role, so a non-dev typing /usage
// directly into the URL gets a 403 → empty state.
const devNavItems = [
  { id: 'usage',    icon: Gauge,      labelKey: 'nav.usage',    path: '/usage'    },
  { id: 'feedback', icon: ThumbsDown, labelKey: 'nav.feedback', path: '/feedback' },
];

const bottomNavItems = [
  { id: 'settings',  icon: Settings,  labelKey: 'nav.settings',  path: '/settings'  },
];

interface SidebarItem { id: string; icon: LucideIcon; labelKey: string; path: string }

// One sidebar nav row. When the sidebar is collapsed (icon-only) the label
// lives in a styled tooltip; when expanded the label renders inline so no
// tooltip is shown. Shared by the main + bottom nav lists.
function SidebarLink({ item, expanded, onNavigate }: { item: SidebarItem; expanded: boolean; onNavigate: () => void }) {
  const t = useT();
  const link = (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      className={({ isActive }) => `
        flex items-center gap-[12px] px-[12px] h-[40px] rounded-[var(--r2)] group transition-colors duration-150 shrink-0
        ${isActive
          ? 'bg-[var(--accent-soft)] text-[var(--text-1)] border-l-[3px] border-l-[var(--accent)]'
          : 'text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text-1)] border-l-[3px] border-l-transparent'}
        ${!expanded ? 'justify-center border-l-0 px-0' : ''}
      `}
    >
      {({ isActive }) => (
        <>
          <item.icon size={20} className={isActive ? 'text-[var(--accent)]' : 'text-[var(--text-3)] group-hover:text-[var(--text-1)] transition-colors shrink-0'} />
          {expanded && (
            <span className="font-[family-name:var(--font-display)] text-[14px] whitespace-nowrap">
              {t(item.labelKey)}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
  return expanded
    ? link
    : <Tooltip content={t(item.labelKey)} width={150} className="relative block">{link}</Tooltip>;
}

export function Sidebar({ isCollapsed, setIsCollapsed }: SidebarProps) {
  const t = useT();
  const clearTrail = useTrailStore(s => s.clear);
  const isDeveloper        = useIsDeveloper();
  const devInspectorOn     = useChatStore(s => s.devInspectorEnabled);
  const [isHovered, setIsHovered] = useState(false);

  // Width now ONLY depends on isCollapsed
  const isWidthExpanded = !isCollapsed;
  // Usage entry needs BOTH the developer role (server-side gate on the
  // /v1/chat/admin/usage endpoint anyway) AND the dev-inspector toggle in
  // Settings. The toggle lets a developer hide their dev-only UI to see what
  // a regular user sees — including hiding the Usage entry. Backend still
  // 403s any non-developer that types `/usage` directly into the URL.
  const navItems = (isDeveloper && devInspectorOn) ? [...baseNavItems, ...devNavItems] : baseNavItems;

  return (
    <aside 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        bg-[var(--surface)] border-r-[1px] border-[var(--border)]
        flex flex-col transition-all duration-300 z-50 h-full
        ${isWidthExpanded ? 'w-[240px]' : 'w-[68px]'}
        shrink-0 relative
      `}
    >
      {/* Sidebar Header */}
      <div className={`
        px-[16px] flex items-center h-[52px] border-b-[1px] border-[var(--border)] shrink-0 overflow-hidden
        ${isWidthExpanded ? 'justify-between' : 'justify-center'}
      `}>
        {!isWidthExpanded ? (
          // Collapsed State Header
          <div className="relative w-[32px] h-[32px] flex items-center justify-center">
            {isHovered ? (
              <Tooltip content={t('layout.expandSidebar', 'Expand Sidebar')} width={160} className="relative inline-flex">
                <button
                  onClick={() => setIsCollapsed(false)}
                  className="p-[6px] rounded-[var(--r2)] text-[var(--accent)] bg-[var(--accent-soft)] hover:text-[var(--text-1)] transition-all animate-in fade-in zoom-in duration-200"
                >
                  <DockLeftIcon className="rotate-180 w-[20px] h-[20px]" />
                </button>
              </Tooltip>
            ) : (
              <div className="w-[32px] h-[32px] bg-[var(--accent)] rounded-[var(--r1)] shrink-0 flex items-center justify-center shadow-sm transition-all">
                <Landmark size={18} className="text-white" />
              </div>
            )}
          </div>
        ) : (
          // Expanded State Header
          <>
            <div className="flex items-center gap-[12px]">
              <div className="w-[32px] h-[32px] bg-[var(--accent)] rounded-[var(--r1)] shrink-0 flex items-center justify-center shadow-sm">
                <Landmark size={18} className="text-white" />
              </div>
              <span className="font-[family-name:var(--font-display)] font-bold text-[15px] text-[var(--text-1)] uppercase tracking-tight whitespace-nowrap">
                Parlumina
              </span>
            </div>
            <Tooltip content={t('layout.collapseSidebar', 'Collapse Sidebar')} width={160} className="relative inline-flex">
              <button
                onClick={() => setIsCollapsed(true)}
                className="p-[6px] rounded-[var(--r2)] text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text-1)] transition-colors"
              >
                <DockLeftIcon className="w-[20px] h-[20px]" />
              </button>
            </Tooltip>
          </>
        )}
      </div>

      <nav className="flex-1 py-[16px] px-[12px] flex flex-col gap-[4px] overflow-y-auto overflow-x-hidden custom-scrollbar">
        {navItems.map(item => (
          <SidebarLink key={item.id} item={item} expanded={isWidthExpanded} onNavigate={clearTrail} />
        ))}
      </nav>

      <div className="px-[12px] pb-[12px] border-t-[0px] border-[var(--border)] shrink-0 pt-[8px]">
        {bottomNavItems.map(item => (
          <SidebarLink key={item.id} item={item} expanded={isWidthExpanded} onNavigate={clearTrail} />
        ))}
      </div>
    </aside>
  );
}

// ---- Shell ----
interface ShellProps {
  children: React.ReactNode;
}

export function Shell({ children }: ShellProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-[var(--bg)] font-[family-name:var(--font-display)] text-[var(--text-2)] transition-colors duration-200">
      <Sidebar isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
      
      <div className="flex flex-col flex-1 min-w-0 h-full overflow-hidden">
        <Topbar
          onMenuToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isSidebarCollapsed={isSidebarCollapsed}
        />
        <ResearchAnchorBanner />
        <NavigationTrail />

        <main className="flex-1 overflow-y-auto p-[32px] custom-scrollbar">
          <div className="mx-auto max-w-[1200px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
